import { APP_SETTINGS } from '../../config/appSettings'
import type {
  GoogleKeepArchiveEntry,
  GoogleKeepIntakeIssue,
  GoogleKeepIntakeIssueCode,
  GoogleKeepIntakeValidation,
} from './types'

const ZIP_MAGIC_PREFIXES = [
  [0x50, 0x4b, 0x03, 0x04],
  [0x50, 0x4b, 0x05, 0x06],
  [0x50, 0x4b, 0x07, 0x08],
] as const

function addIssue(
  issues: GoogleKeepIntakeIssue[],
  code: GoogleKeepIntakeIssueCode,
): void {
  const existing = issues.find((issue) => issue.code === code)
  if (existing) existing.count += 1
  else issues.push({ code, count: 1 })
}

function safeRelativePath(value: string): { depth: number; normalized: string } | null {
  const path = value.replaceAll('\\', '/')
  if (!path || path.includes('\0') || path.startsWith('/') || /^[a-z]:\//i.test(path)) return null
  const segments: string[] = []
  for (const segment of path.split('/')) {
    if (!segment || segment === '.') continue
    if (segment === '..') return null
    segments.push(segment)
  }
  if (!segments.length) return null
  return { depth: segments.length, normalized: segments.join('/') }
}

export function hasZipMagic(bytes: Uint8Array): boolean {
  return ZIP_MAGIC_PREFIXES.some(
    (prefix) => prefix.every((byte, index) => bytes[index] === byte),
  )
}

export function validateGoogleKeepIntake(
  entries: readonly GoogleKeepArchiveEntry[],
  archiveBytes: number | null,
  magicBytes?: Uint8Array,
): GoogleKeepIntakeValidation {
  const settings = APP_SETTINGS.imports.googleKeep
  const issues: GoogleKeepIntakeIssue[] = []
  if (archiveBytes !== null && (
    !Number.isSafeInteger(archiveBytes)
    || archiveBytes < 0
    || archiveBytes > settings.archiveMaxBytes
  )) addIssue(issues, archiveBytes > settings.archiveMaxBytes ? 'archive-too-large' : 'invalid-entry-metadata')
  if (magicBytes && !hasZipMagic(magicBytes)) addIssue(issues, 'zip-magic-invalid')
  if (entries.length > settings.maxEntries) addIssue(issues, 'entry-count-exceeded')

  const paths = new Set<string>()
  let expandedBytes = 0
  for (const entry of entries) {
    const compressedBytes = entry.compressedBytes
    const sizesValid = Number.isSafeInteger(entry.uncompressedBytes)
      && entry.uncompressedBytes >= 0
      && (compressedBytes === undefined
        || (Number.isSafeInteger(compressedBytes) && compressedBytes >= 0))
    if (!sizesValid) {
      addIssue(issues, 'invalid-entry-metadata')
      continue
    }
    const safePath = safeRelativePath(entry.relativePath)
    if (!safePath) {
      addIssue(issues, 'invalid-entry-path')
      continue
    }
    const caseFoldedPath = safePath.normalized.toLocaleLowerCase('en-US')
    if (paths.has(caseFoldedPath)) addIssue(issues, 'duplicate-entry-path')
    else paths.add(caseFoldedPath)
    if (new TextEncoder().encode(safePath.normalized).byteLength > settings.maxPathBytes) {
      addIssue(issues, 'path-too-long')
    }
    if (safePath.depth > settings.maxNestingDepth) addIssue(issues, 'nesting-depth-exceeded')
    if (entry.encrypted) addIssue(issues, 'encrypted-entry')
    if (entry.isSymlink) addIssue(issues, 'symlink-entry')
    if (!entry.isDirectory && entry.uncompressedBytes > settings.maxEntryBytes) {
      addIssue(issues, 'entry-too-large')
    }
    expandedBytes += entry.uncompressedBytes
    if (
      compressedBytes !== undefined
      && entry.uncompressedBytes > 0
      && (compressedBytes === 0
        || entry.uncompressedBytes / compressedBytes > settings.maxCompressionRatio)
    ) addIssue(issues, 'compression-ratio-exceeded')
  }
  if (expandedBytes > settings.expandedMaxBytes) addIssue(issues, 'expanded-size-exceeded')
  return { accepted: issues.length === 0, issues }
}
